<template>
  <div class="container mt-5">
    <h3 class="text-center mb-4">Previous Games Leaderboards</h3>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center mb-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <!-- Previous Games Table -->
    <div v-else-if="previousGames.length > 0" class="table-responsive">
      <table class="table table-striped table-hover">
        <thead class="table-dark">
          <tr>
            <th>Date</th>
            <th>Location</th>
            <th>Teams</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="game in previousGames" :key="game.id">
            <td>{{ formatDate(game.date) }}</td>
            <td>{{ game.locationName }}</td>
            <td>{{ game.teamCount }}</td>
            <td>
              <button 
                @click="viewLeaderboard(game)" 
                class="btn btn-primary btn-sm"
                :disabled="game.teamCount === 0"
              >
                View Leaderboard
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- No Games Message -->
    <div v-else class="text-center">
      <p class="text-muted">No previous games found.</p>
    </div>
    
    <!-- Leaderboard Modal -->
    <div v-if="showLeaderboardModal" class="modal fade show" tabindex="-1" style="display: block;">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Leaderboard - {{ selectedGame?.locationName }} - {{ formatDate(selectedGame?.date) }}
            </h5>
            <button type="button" class="btn-close" @click="closeLeaderboardModal"></button>
          </div>
          <div class="modal-body">
            <!-- Loading State for Leaderboard -->
            <div v-if="leaderboardLoading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading leaderboard...</span>
              </div>
            </div>
            
            <!-- Leaderboard Table -->
            <div v-else-if="leaderboardData.length > 0" class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Team Name</th>
                    <th>Total Points</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(team, index) in leaderboardData" :key="team.teamId">
                    <td>
                      <span class="rank-badge">{{ index + 1 }}</span>
                    </td>
                    <td>{{ team.teamName }}</td>
                    <td class="fw-bold">{{ team.totalScore }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- No Teams Message -->
            <div v-else class="text-center">
              <p class="text-muted">No teams found for this game.</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeLeaderboardModal">Close</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div v-if="showLeaderboardModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { db } from '@/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import moment from 'moment-timezone';

export default {
  name: 'PreviousGamesLeaderboard',
  setup() {
    const isLoading = ref(true);
    const leaderboardLoading = ref(false);
    const previousGames = ref([]);
    const showLeaderboardModal = ref(false);
    const selectedGame = ref(null);
    const leaderboardData = ref([]);

    const fetchPreviousGames = async () => {
      try {
        isLoading.value = true;
        
        // Get all games
        const gamesSnapshot = await getDocs(collection(db, 'games'));
        const gamesData = gamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Get all locations for reference
        const locationsSnapshot = await getDocs(collection(db, 'locations'));
        const locations = locationsSnapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data();
          return acc;
        }, {});

        // Process each game to get team count and location name
        const gamesWithStats = await Promise.all(
          gamesData.map(async (game) => {
            const locationName = locations[game.location]?.name || 'Unknown Location';
            
            // Count teams for this game
            const teamsQuery = query(
              collection(db, 'teams'),
              where('gameId', '==', game.id)
            );
            const teamsSnapshot = await getDocs(teamsQuery);
            const teamCount = teamsSnapshot.size;

            return {
              ...game,
              locationName,
              teamCount
            };
          })
        );

        // Sort by date (most recent first) and filter out games with no teams
        previousGames.value = gamesWithStats
          .filter(game => game.teamCount > 0)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
          
      } catch (error) {
        console.error('Error fetching previous games:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const viewLeaderboard = async (game) => {
      selectedGame.value = game;
      showLeaderboardModal.value = true;
      leaderboardLoading.value = true;
      leaderboardData.value = [];

      try {
        // Get all teams for this game
        const teamsQuery = query(
          collection(db, 'teams'),
          where('gameId', '==', game.id)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        
        // Get submissions for each team to calculate total scores
        const teamScores = [];
        
        for (const teamDoc of teamsSnapshot.docs) {
          const teamData = teamDoc.data();
          
          // Get all submissions for this team in this game
          const submissionsQuery = query(
            collection(db, `games/${game.id}/submissions`),
            where('teamId', '==', teamData.teamId)
          );
          const submissionsSnapshot = await getDocs(submissionsQuery);
          
          let totalScore = 0;
          submissionsSnapshot.forEach(submissionDoc => {
            const submissionData = submissionDoc.data();
            totalScore += Number(submissionData.totalScore) || 0;
          });
          
          teamScores.push({
            teamId: teamData.teamId,
            teamName: teamData.teamName,
            totalScore: totalScore
          });
        }
        
        // Sort by total score (highest first)
        leaderboardData.value = teamScores.sort((a, b) => b.totalScore - a.totalScore);
        
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
      } finally {
        leaderboardLoading.value = false;
      }
    };

    const closeLeaderboardModal = () => {
      showLeaderboardModal.value = false;
      selectedGame.value = null;
      leaderboardData.value = [];
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      return moment(dateString).format('MMM D, YYYY');
    };

    onMounted(fetchPreviousGames);

    return {
      isLoading,
      leaderboardLoading,
      previousGames,
      showLeaderboardModal,
      selectedGame,
      leaderboardData,
      viewLeaderboard,
      closeLeaderboardModal,
      formatDate
    };
  }
};
</script>

<style scoped>
.table-responsive {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table th {
  background-color: #343a40;
  color: white;
  border: none;
  padding: 12px 8px;
}

.table td {
  padding: 12px 8px;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.rank-badge {
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  background-color: #007bff;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 12px 12px 0 0;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
  border-radius: 0 0 12px 12px;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .table th,
  .table td {
    padding: 8px 6px;
  }
  
  .rank-badge {
    width: 25px;
    height: 25px;
    line-height: 25px;
    font-size: 12px;
  }
}
</style>
